import type { CSSProperties, Ref } from 'vue';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  hide,
  type Placement,
  type ReferenceElement,
} from '@floating-ui/vue';
import { computed, ref, type MaybeRef } from 'vue';

export type FloatingWidthBehaviour = 'auto' | 'match' | 'min-match';

export interface UseFloatingPositionOptions {
  /** Реактивный флаг открытия — управляет жизненным циклом autoUpdate. */
  open: Ref<boolean>;
  /** Размещение поповера относительно триггера. По умолчанию `bottom-start`. */
  placement?: MaybeRef<Placement>;
  /** Отступ между триггером и поповером в пикселях. По умолчанию 8. */
  offset?: number;
  /** Внутренний отступ от краёв viewport / scroll-родителя. По умолчанию 8. */
  padding?: number;
  /** Включить flip (переворот в сторону, где есть место). По умолчанию true. */
  flip?: boolean;
  /** Включить shift (горизонтальный сдвиг для удержания во viewport). По умолчанию true. */
  shift?: boolean;
  /** Жёсткий потолок высоты в пикселях. Если не задан — берём всё доступное место. */
  maxHeight?: number;
  /** Жёсткий потолок ширины в пикселях (только для `widthBehaviour !== 'auto'`). */
  maxWidth?: number;
  /**
   * Поведение ширины поповера:
   * - `auto` — ширина не задаётся (default).
   * - `match` — `width = reference.width`.
   * - `min-match` — `min-width = reference.width`, `max-width = availableWidth` (или `maxWidth`).
   */
  widthBehaviour?: FloatingWidthBehaviour;
}

export interface UseFloatingPositionReturn {
  reference: Ref<ReferenceElement | null>;
  floating: Ref<HTMLElement | null>;
  floatingStyles: Ref<CSSProperties>;
  placement: Ref<Placement>;
  isPositioned: Ref<boolean>;
  isReferenceHidden: Ref<boolean>;
  /** Реальная допустимая высота поповера. `null` до первого позиционирования. */
  availableHeight: Ref<number | null>;
  update: () => void;
}

export function useFloatingPosition(opts: UseFloatingPositionOptions): UseFloatingPositionReturn {
  const reference = ref<ReferenceElement | null>(null);
  const floating = ref<HTMLElement | null>(null);
  const availableHeight = ref<number | null>(null);

  const offsetPx = opts.offset ?? 8;
  const padding = opts.padding ?? 8;
  const widthBehaviour = opts.widthBehaviour ?? 'auto';

  const middleware = computed(() => {
    const list = [offset(offsetPx)];

    if (opts.flip !== false) {
      list.push(flip({ padding }));
    }
    if (opts.shift !== false) {
      list.push(shift({ padding }));
    }

    list.push(
      size({
        padding,
        apply({ availableHeight: ah, availableWidth, elements, rects }) {
          const cap = opts.maxHeight ? Math.min(ah, opts.maxHeight) : ah;
          availableHeight.value = Math.max(0, cap);

          if (widthBehaviour === 'match') {
            const target = opts.maxWidth ? Math.min(rects.reference.width, opts.maxWidth) : rects.reference.width;
            Object.assign(elements.floating.style, {
              width: `${target}px`,
            });
          } else if (widthBehaviour === 'min-match') {
            const cappedMax = opts.maxWidth ? Math.min(availableWidth, opts.maxWidth) : availableWidth;
            Object.assign(elements.floating.style, {
              minWidth: `${rects.reference.width}px`,
              maxWidth: `${Math.max(rects.reference.width, cappedMax)}px`,
            });
          }
        },
      }),
    );

    list.push(hide({ padding }));
    return list;
  });

  const { floatingStyles, placement, isPositioned, middlewareData, update } = useFloating(reference, floating, {
    open: opts.open,
    placement: opts.placement ?? 'bottom-start',
    strategy: 'fixed',
    // Используем `top`/`left` вместо `transform: translate(...)`,
    // иначе CSS-transition потребителя (`scale-95`, `translateY` и т.д.)
    // перезаписывает позиционирующий transform и поповер «прилетает» из 0,0.
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const isReferenceHidden = computed(() => middlewareData.value.hide?.referenceHidden === true);

  return {
    reference,
    floating,
    floatingStyles,
    placement,
    isPositioned,
    isReferenceHidden,
    availableHeight,
    update,
  };
}
